/**
 * AuiGeoMapChart — Choropleth map chart for geographic data.
 *
 * Uses ECharts directly (not echarts-for-react) because the wrapper
 * library's double-init with explicit width/height causes mouse
 * coordinate misalignment on map charts. By initialising the ECharts
 * instance ourselves we get pixel-perfect hit-testing.
 *
 * Agent-facing props (via node.component.props):
 *   mapName         – registered map name (e.g. "USA", "CaliforniaZips")
 *   data            – array of { name, value } pairs
 *   height          – chart height in px (default 480)
 *   title           – optional chart title
 *   roam            – enable pan/zoom (default false)
 *   visualMapRange  – optional [min, max] for the color scale
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { echarts } from '@/lib/charts/echarts-setup';
import { useChartTheme } from '@/lib/charts/useChartTheme';
import { ensureMapRegistered, getMapNameProperty } from '@/lib/charts/geo-registry';
import type { AuiRendererProps } from '../../registry';
import type { EChartsOption } from 'echarts';

const TEST_ID = 'aui-geo-map-chart';

export function AuiGeoMapChart({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const mapName = props.mapName as string | undefined;
  const data = useMemo(
    () => (props.data ?? []) as { name: string; value: number }[],
    [props.data],
  );
  const height = (props.height as number) ?? 480;
  const title = props.title as string | undefined;
  const roam = (props.roam as boolean) ?? false;
  const visualMapRange = props.visualMapRange as [number, number] | undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof echarts.init> | null>(null);
  const { themeName } = useChartTheme();

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the map GeoJSON
  useEffect(() => {
    if (!mapName) {
      setError('Missing mapName prop');
      return;
    }
    let cancelled = false;
    setReady(false);
    setError(null);

    ensureMapRegistered(mapName)
      .then(() => { if (!cancelled) setReady(true); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); });

    return () => { cancelled = true; };
  }, [mapName]);

  const nameProperty = mapName ? getMapNameProperty(mapName) : undefined;

  // Build the option
  const buildOption = useCallback((): EChartsOption | null => {
    if (!ready || !mapName) return null;

    const values = data.map((d) => d.value);
    const min = visualMapRange?.[0] ?? Math.min(...values, 0);
    const max = visualMapRange?.[1] ?? Math.max(...values, 0);

    return {
      title: title
        ? { text: title, left: 'center' }
        : undefined,
      tooltip: {
        trigger: 'item',
        position: 'top',
        formatter: (params: unknown) => {
          const p = params as { name: string; value?: number };
          if (p.value == null || Number.isNaN(p.value)) return p.name;
          return `${p.name}: ${p.value}`;
        },
      },
      visualMap: {
        left: 'left',
        bottom: 16,
        min,
        max,
        calculable: true,
        inRange: { color: ['#e0f2fe', '#0284c7'] },
        text: [`${max}`, `${min}`],
      },
      series: [
        {
          name: mapName,
          type: 'map',
          map: mapName,
          roam,
          ...(nameProperty ? { nameProperty } : {}),
          data,
          label: { show: false },
          itemStyle: {
            borderColor: '#aaa',
            borderWidth: 0.5,
          },
          emphasis: {
            label: { show: false },
            itemStyle: {
              areaColor: '#8b5cf6',
              borderColor: '#6d28d9',
              borderWidth: 2,
            },
          },
          select: {
            disabled: true,
          },
        },
      ],
    };
  }, [ready, mapName, data, title, roam, nameProperty, visualMapRange]);

  // Init / update / dispose the ECharts instance directly
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !ready) return;

    const option = buildOption();
    if (!option) return;

    // Init if needed
    if (!chartRef.current) {
      chartRef.current = echarts.init(el, themeName, { renderer: 'canvas' });
    }

    chartRef.current.setOption(option, true);

    // Resize observer for responsive sizing
    const ro = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
    };
  }, [ready, buildOption, themeName]);

  // Update theme when it changes
  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    const el = containerRef.current;
    const option = chartRef.current.getOption();
    chartRef.current.dispose();
    chartRef.current = echarts.init(el, themeName, { renderer: 'canvas' });
    chartRef.current.setOption(option, true);
  }, [themeName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  if (error) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid={TEST_ID}>
        <p className="text-sm text-destructive">
          Failed to load map: {error}
        </p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid={TEST_ID}>
        <Skeleton className="w-full" style={{ height }} />
      </div>
    );
  }

  return (
    <div data-testid={TEST_ID}>
      <div ref={containerRef} style={{ height, width: '100%' }} />
    </div>
  );
}
